import { StaticDatabaseContext } from "@-/dev.improve.haniverse.contexts.database.static";
import type {
  StaticDatabase,
  StaticDatabaseHandle,
} from "@-/dev.improve.haniverse.database.static";
import type { FindManyConfig } from "@-/dev.improve.haniverse.lib.database";
import type { ImportMetaLocation } from "@-/dev.improve.haniverse.lib.logger";
import type { QueryFunction } from "@-/com.npmjs.tanstack__react-query";
import { queryCachePersisters } from "@-/dev.improve.haniverse.lib.storage/query-cache-persisters";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { stripRelationalConfigColumns } from "#strip-relational-config-columns";
import type { StaticColumnsAgnosticConfig, StaticQueryTable, StaticRelationalRow } from "#types";

import {
  STATIC_QUERY_GC_TIME,
  STATIC_QUERY_META,
  STATIC_QUERY_STALE_TIME,
} from "#static-query-cache-options";

// `location` is the caller's `import.meta.location`, threaded in via
// `staticQueries(import.meta.location)`, so `db(location)` attributes query logs
// to the read's origin instead of this factory file. `null` opts out.
export function createStaticFindManyQuery<TTable extends StaticQueryTable>(
  location: ImportMetaLocation,
  table: TTable,
) {
  type FindManyQueryConfig = FindManyConfig<StaticDatabaseHandle, TTable>;
  // The joined row for a specific caller config `Cfg`, reconstructed from the
  // static schema's relational config so `config.with` relations are reflected
  // in the type.
  type RowFor<Cfg extends true | Record<string, unknown>> = StaticRelationalRow<TTable, Cfg>;

  type FindManyQueryKey<
    WithConfig extends FindManyQueryConfig & StaticColumnsAgnosticConfig,
    Config extends FindManyQueryConfig & StaticColumnsAgnosticConfig,
  > = readonly [
    "staticQuery",
    TTable,
    "findMany",
    WithConfig,
    Config,
    { dataIdentity: string; db: StaticDatabase },
  ];

  // `useGetQueryKey` binds the database once (at the top level of a hook) from
  // `withConfig`, then the returned getter builds a key from a per-lookup
  // `config` supplied later — inside a callback, for many different `where`s.
  // That late binding lets prefetch/mutation code capture a static `with` at
  // render while keying each individual read at fetch time. (The static DB is
  // immutable, so there is no change-version to subscribe to.)
  //
  // Both configs are column-stripped HERE, the single choke point every key
  // flows through: the query functions read their fetch config back off the key,
  // so stripping at key-build time makes cache entries column-agnostic.
  function useGetQueryKey<
    const WithConfig extends FindManyQueryConfig & StaticColumnsAgnosticConfig,
  >(withConfig: WithConfig) {
    // `db` is a function, which the key hash drops; `dataIdentity` is what makes a cached (and
    // persisted) entry specific to the static source and artifact build it was read from.
    const [db, dataIdentity] = useContextSelectors(
      StaticDatabaseContext,
      (context) => context.db,
      (context) => context.dataIdentity,
    );
    const strippedWithConfig = stripRelationalConfigColumns(withConfig);

    return <const Config extends FindManyQueryConfig & StaticColumnsAgnosticConfig>(
      config: Config,
    ): FindManyQueryKey<WithConfig, Config> =>
      [
        "staticQuery",
        table,
        "findMany",
        strippedWithConfig,
        stripRelationalConfigColumns(config),
        { dataIdentity, db },
      ] as const;
  }

  async function queryFn({
    queryKey,
  }: {
    queryKey: ReturnType<ReturnType<typeof useGetQueryKey>>;
    // The pair form reads its config at runtime from the key, so its config type
    // isn't statically known — resolve to the base (relation-less) row.
  }): Promise<RowFor<Record<string, never>>[]> {
    const withConfig = queryKey[3];
    const config = queryKey[4];
    const { db } = queryKey[5];
    const queryTable = db(location).query[table];
    // `table` and `config` are paired in the query key; drizzle's per-table
    // `KnownKeysOnly` generic cannot be recovered through the factory parameter.
    /* eslint-disable typescript/no-unsafe-type-assertion -- see above */
    return queryTable.findMany({
      ...withConfig,
      ...config,
    } as Parameters<typeof queryTable.findMany>[0]) as unknown as Promise<
      RowFor<Record<string, never>>[]
    >;
    /* eslint-enable typescript/no-unsafe-type-assertion -- Restore checked assertions after this documented bridge. */
  }

  /**
   * Builds query options from a render-bound key getter and a lookup-specific
   * config. `useGetQueryKey` captures the database and the static relation shape
   * once; this pure function is safe to call later from navigator/prefetch
   * callbacks. Carries the immutable-DB cache knobs (`staleTime`/`gcTime`).
   */
  function getQueryOptions<
    const WithConfig extends FindManyQueryConfig & StaticColumnsAgnosticConfig,
    const Config extends FindManyQueryConfig & StaticColumnsAgnosticConfig,
  >(getQueryKey: (config: Config) => FindManyQueryKey<WithConfig, Config>, config: Config) {
    const queryKey = getQueryKey(config);
    const typedQueryFn: QueryFunction<RowFor<WithConfig & Config>[], typeof queryKey> = async ({
      queryKey: queryFnQueryKey,
    }) =>
      // The key getter carries the precise static `with` and lookup config types.
      // `queryFn` intentionally exposes the relation-agnostic base type because
      // it can also be called directly without a typed options builder.
      // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- refined by the paired key getter above
      (await queryFn({ queryKey: queryFnQueryKey })) as RowFor<WithConfig & Config>[];

    return {
      gcTime: STATIC_QUERY_GC_TIME,
      meta: STATIC_QUERY_META,
      persister: queryCachePersisters().staticQueryPersister<
        RowFor<WithConfig & Config>[],
        typeof queryKey
      >,
      queryFn: typedQueryFn,
      queryKey,
      staleTime: STATIC_QUERY_STALE_TIME,
    };
  }

  // Single-shot entry point. Capturing the literal `Cfg` lets the return type
  // reflect `Cfg["with"]` joins. The split pair above remains for prefetch and
  // composition callers that must defer `where`.
  function useQueryOptions<const Cfg extends FindManyQueryConfig & StaticColumnsAgnosticConfig>(
    config: Cfg,
  ) {
    const getQueryKey = useGetQueryKey(config);
    const { persister, queryFn: typedQueryFn } = getQueryOptions(getQueryKey, config);

    return {
      gcTime: STATIC_QUERY_GC_TIME,
      meta: STATIC_QUERY_META,
      persister,
      queryFn: typedQueryFn,
      get queryKey() {
        return getQueryKey(config);
      },
      staleTime: STATIC_QUERY_STALE_TIME,
    };
  }

  return { getQueryOptions, queryFn, useGetQueryKey, useQueryOptions };
}
